import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Truck, Anchor, MapPin, Train, Globe } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_ICONS = { STATE: MapPin, PORT: Anchor, RAIL: Train };


// ==================== NOTIFY CARRIERS MODAL ====================
export const NotifyCarriersModal = ({ load, onClose }) => {
  const [regions, setRegions] = useState([]);
  const [carrierType, setCarrierType] = useState('');
  const [regionIds, setRegionIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/regions`).then(r => setRegions(r.data)).catch(() => {});
  }, []);

  const handleSend = async () => {
    if (!carrierType) { toast.error('Select a carrier type'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/email/notify-carriers`, {
        load_id: load.id,
        carrier_type: carrierType,
        region_ids: regionIds,
      });
      toast.success(res.data.message);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send notifications');
    } finally { setSending(false); }
  };

  const filteredRegions = regions.filter(r =>
    carrierType === 'OTR' ? r.region_type === 'STATE' : (r.region_type === 'PORT' || r.region_type === 'RAIL')
  );

  const toggleRegion = (id) => setRegionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Notify Carriers</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900" data-testid="close-notify-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="bg-zinc-50 border border-zinc-200 p-4 text-sm">
            <strong>#{load.reference_no}</strong> — {load.pickup} to {load.destination} ({load.load_type})
          </div>

          {/* Carrier Type Selection */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Carrier Type</label>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setCarrierType('OTR'); setRegionIds([]); }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
                data-testid="notify-type-otr">
                <Truck className="w-4 h-4" /> OTR
              </button>
              <button type="button" onClick={() => { setCarrierType('DRAYAGE'); setRegionIds([]); }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'DRAYAGE' ? 'bg-orange-600 text-white border-orange-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
                data-testid="notify-type-drayage">
                <Anchor className="w-4 h-4" /> Drayage
              </button>
            </div>
          </div>

          {/* Region Filter */}
          {carrierType && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {carrierType === 'OTR' ? 'Filter by States' : 'Filter by Ports/Ramps'}
                <span className="text-zinc-400 normal-case ml-1">(optional)</span>
              </label>
              <p className="text-xs text-zinc-400 mb-3">
                Leave empty to notify all {carrierType} carriers (including nationwide).
              </p>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {filteredRegions.map(r => {
                  const Icon = REGION_ICONS[r.region_type] || MapPin;
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRegion(r.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold border transition-colors ${
                        regionIds.includes(r.id)
                          ? (carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'bg-orange-600 text-white border-orange-600')
                          : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                      }`}>
                      <Icon className="w-3 h-3" /> {r.name}
                    </button>
                  );
                })}
              </div>
              {regionIds.length === 0 && (
                <div className="flex items-center gap-1 mt-2 text-xs text-green-600 font-medium">
                  <Globe className="w-3 h-3" /> Will notify all {carrierType} carriers
                </div>
              )}
            </div>
          )}

          <button onClick={handleSend} disabled={sending || !carrierType}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50"
            data-testid="send-notify-btn">
            <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Notifications'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== SEND DIGEST MODAL ====================
export const SendDigestModal = ({ onClose }) => {
  const [regions, setRegions] = useState([]);
  const [carrierType, setCarrierType] = useState('');
  const [regionIds, setRegionIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/regions`).then(r => setRegions(r.data)).catch(() => {});
  }, []);

  const handleSend = async () => {
    if (!carrierType) { toast.error('Select a carrier type'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/email/send-digest`, {
        carrier_type: carrierType,
        region_ids: regionIds,
      });
      toast.success(res.data.message);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send digest');
    } finally { setSending(false); }
  };

  const filteredRegions = regions.filter(r =>
    carrierType === 'OTR' ? r.region_type === 'STATE' : (r.region_type === 'PORT' || r.region_type === 'RAIL')
  );

  const toggleRegion = (id) => setRegionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-zinc-200">
          <h2 className="text-xl font-black text-zinc-900">Send Load Digest</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-900" data-testid="close-digest-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-zinc-600">Send all active loads to carriers based on type and region.</p>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Carrier Type</label>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setCarrierType('OTR'); setRegionIds([]); }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
                data-testid="digest-type-otr">
                <Truck className="w-4 h-4" /> OTR
              </button>
              <button type="button" onClick={() => { setCarrierType('DRAYAGE'); setRegionIds([]); }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 font-bold text-sm uppercase border-2 transition-colors ${carrierType === 'DRAYAGE' ? 'bg-orange-600 text-white border-orange-600' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'}`}
                data-testid="digest-type-drayage">
                <Anchor className="w-4 h-4" /> Drayage
              </button>
            </div>
          </div>

          {carrierType && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                {carrierType === 'OTR' ? 'Filter by States' : 'Filter by Ports/Ramps'}
              </label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {filteredRegions.map(r => {
                  const Icon = REGION_ICONS[r.region_type] || MapPin;
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRegion(r.id)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold border transition-colors ${
                        regionIds.includes(r.id)
                          ? (carrierType === 'OTR' ? 'bg-blue-600 text-white border-blue-600' : 'bg-orange-600 text-white border-orange-600')
                          : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                      }`}>
                      <Icon className="w-3 h-3" /> {r.name}
                    </button>
                  );
                })}
              </div>
              {regionIds.length === 0 && (
                <div className="flex items-center gap-1 mt-2 text-xs text-green-600 font-medium">
                  <Globe className="w-3 h-3" /> Will send to all {carrierType} carriers
                </div>
              )}
            </div>
          )}

          <button onClick={handleSend} disabled={sending || !carrierType}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 font-bold uppercase tracking-wide disabled:opacity-50"
            data-testid="send-digest-btn">
            <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Digest'}
          </button>
        </div>
      </div>
    </div>
  );
};
